import type { WhatsappIntegration, WhatsappProviderSummary } from '@chukua/common';
import { Alert, Drawer } from 'antd';
import { FormCardSkeleton } from '../../../../../shared/components/FormCardSkeleton';
import { useWhatsappIntegrationSheet } from '../hooks/use-whatsapp-integration-sheet';
import { WhatsappIntegrationForm } from './WhatsappIntegrationForm';

interface WhatsappIntegrationSheetProps {
  providers: WhatsappProviderSummary[];
  /** The provider sending now, named when installing another would replace it. */
  sendingName: string | undefined;
  /** The provider being installed, or undefined for the settings of `integrationId`. */
  providerKey: string | undefined;
  integrationId: string | undefined;
  onClose: () => void;
  /** Called after a save, a switch-off or an uninstall, so the list behind the sheet shows it. */
  onChanged: () => void;
  onStopSending: (integration: WhatsappIntegration) => Promise<void>;
  onUninstall: (integration: WhatsappIntegration) => Promise<void>;
}

/** Install a WhatsApp provider, or change one's settings: its own fields, checked with the provider before they are saved, and write-only secrets. */
export function WhatsappIntegrationSheet({
  providers,
  sendingName,
  providerKey,
  integrationId,
  onClose,
  onChanged,
  onStopSending,
  onUninstall,
}: WhatsappIntegrationSheetProps) {
  const done = () => {
    onChanged();
    onClose();
  };
  const sheet = useWhatsappIntegrationSheet({ providers, providerKey, integrationId, onDone: done });
  const title = sheet.integration ? sheet.integration.displayName : `Add ${sheet.provider?.displayName ?? 'a WhatsApp provider'}`;

  return (
    <Drawer open onClose={onClose} title={title} size={480}>
      {sheet.isLoading && <FormCardSkeleton label="Loading the WhatsApp provider" />}
      {!sheet.isLoading && !sheet.provider && (
        <Alert
          type="error"
          showIcon
          title="This WhatsApp provider isn't available"
          description="It may not be part of this release, or the link is out of date."
        />
      )}
      {!sheet.isLoading && sheet.provider && (
        <WhatsappIntegrationForm
          sheet={sheet}
          provider={sheet.provider}
          sendingName={sendingName}
          onStopSending={async (integration) => {
            await onStopSending(integration);
            onClose();
          }}
          onUninstall={async (integration) => {
            await onUninstall(integration);
            onClose();
          }}
        />
      )}
    </Drawer>
  );
}
